/**
 * Two-Factor Authentication Middleware
 * Handles 2FA requirement detection, request pausing, and retry logic
 *
 * Flow:
 * 1. API call receives TWOFA_REQUIRED response
 * 2. Middleware stores pending request in Zustand store
 * 3. Modal automatically opens (via ModalContext watching store)
 * 4. User enters code in modal
 * 5. Modal executes pending request with 2FA headers
 * 6. Result is stored in Zustand
 * 7. Original API call promise resolves with result
 */

import { TwoFactorMethod } from '@/types/misc';
import { useTwoFactorStore } from '../../stores/twoFactorStore';
import { ApiResponse, ApiResult } from '../apiTypes';

/**
 * Backend error code indicating 2FA is required
 * Matches the error field in API responses when 2FA verification is needed
 */
export const TWOFA_REQUIRED_ERROR = 'TWOFA_REQUIRED';

/**
 * Backend error code indicating 2FA verification failed (wrong code)
 * Matches the error field in API responses when user provides incorrect 2FA code
 */
export const TWOFA_VERIFICATION_FAILED_ERROR = 'TWOFA_VERIFICATION_FAILED';

/**
 * HTTP headers used for 2FA verification
 */
export const TWOFA_HEADERS = {
  SESSION_ID: 'X-2FA-Session-Id',
  SESSION_CODE: 'X-2FA-Session-Code',
} as const;

/**
 * Pending request that can be replayed with a 2FA code
 * The execute function is a closure that captures the original request context
 */
type PendingRequest = {
  execute: (code: string) => Promise<ApiResult<unknown>>;
};

/**
 * Wait for pending 2FA request to complete
 *
 * This function pauses the API call and waits for the user to complete 2FA verification.
 * It polls the Zustand store until:
 * - apiResult is available AND pendingRequest is cleared (verification completed)
 * - Store is cleared without result (user cancelled)
 *
 * The polling interval (100ms) provides responsive detection while being efficient.
 * Store subscriptions provide immediate notification when state changes.
 *
 * @returns Promise that resolves with the API result once verification completes
 * @throws Error if verification is cancelled
 */
async function waitForTwoFAResult(): Promise<ApiResult<unknown>> {
  return new Promise<ApiResult<unknown>>((resolve, reject) => {
    // Check initial state before subscribing (in case result is already available)
    const initialState = useTwoFactorStore.getState();
    if (initialState.apiResult !== undefined && !initialState.pendingRequest) {
      resolve(initialState.apiResult);
      return;
    }
    if (!initialState.isRequired && !initialState.pendingRequest && !initialState.apiResult && !initialState.error) {
      reject(new Error('2FA verification cancelled'));
      return;
    }

    // Subscribe to store changes for immediate notification
    const unsubscribe = useTwoFactorStore.subscribe((state) => {
      // Result available and pendingRequest cleared - verification complete
      if (state.apiResult !== undefined && !state.pendingRequest) {
        unsubscribe();
        resolve(state.apiResult);
      }

      // Cancelled: Store cleared without result
      if (!state.isRequired && !state.pendingRequest && !state.apiResult && !state.error) {
        unsubscribe();
        reject(new Error('2FA verification cancelled'));
      }
    });
  });
}

/**
 * Check if a response indicates 2FA is required
 *
 * Validates that the response contains:
 * - Error code matching TWOFA_REQUIRED
 * - Session ID in details
 * - 2FA method (SMS or EMAIL) in details
 *
 * @param response - API response to check
 * @returns true if 2FA is required, false otherwise
 */
export function isTwoFARequired<T>(response: ApiResponse<T>): boolean {
  return response.error === TWOFA_REQUIRED_ERROR && !!response.details?.session_id && !!response.details?.twofa_method;
}

/**
 * Extract 2FA session details from response
 *
 * Extracts the session ID and verification method from a 2FA-required or verification-failed response.
 * Returns null if the response doesn't contain valid 2FA details.
 *
 * @param response - API response containing 2FA details
 * @returns Object with sessionId and method, or null if invalid
 */
export function extractTwoFADetails(response: ApiResponse<unknown>): {
  sessionId: string;
  method: TwoFactorMethod;
} | null {
  // Check if response has 2FA-related error and details
  const has2FAError = response.error === TWOFA_REQUIRED_ERROR || response.error === TWOFA_VERIFICATION_FAILED_ERROR;
  if (!has2FAError || !response.details) {
    return null;
  }

  const sessionId = response.details.session_id;
  const method = response.details.twofa_method as TwoFactorMethod;

  // Validate that both session ID and method are present
  if (!sessionId || !method) {
    return null;
  }

  return { sessionId, method };
}

/**
 * Handle initial 2FA requirement - store pending request and wait for user input
 *
 * This is called when the backend first indicates 2FA is required (no code provided yet).
 * It:
 * 1. Extracts session details from the response
 * 2. Creates a closure that can replay the original request with a 2FA code
 * 3. Stores the pending request in Zustand (which triggers modal to open)
 * 4. Waits for user to complete verification
 * 5. Returns the final result once verification completes
 *
 * @param response - API response indicating 2FA is required
 * @param makeRequestWithCode - Function to replay the original request with 2FA code
 * @returns Final API result after 2FA verification
 */
export async function handleTwoFARequirement<T>(
  response: ApiResponse<T>,
  makeRequestWithCode: (code: string) => Promise<ApiResult<T>>
): Promise<ApiResult<T>> {
  // Extract session ID and verification method from response
  const details = extractTwoFADetails(response);
  if (!details) {
    return {
      data: null,
      error: {
        message: 'Invalid 2FA response from server',
      },
    };
  }

  const { sessionId, method } = details;

  // Create a closure that captures the original request context
  // This function will be called by the modal when user submits their code
  // This wrapper stores the result in the store when execution completes
  const executeWithCode: PendingRequest['execute'] = async (code: string) => {
    const result = await makeRequestWithCode(code);

    // Check if result indicates wrong code (verification failed)
    const isWrongCode =
      result.error?.message?.includes(TWOFA_VERIFICATION_FAILED_ERROR) || result.error?.details?.session_id;

    if (isWrongCode) {
      // Don't clear pendingRequest - user can retry
      return result;
    }

    // Success or other error - store result in store so waitForTwoFAResult() can pick it up
    const store = useTwoFactorStore.getState();
    if (store.pendingRequest) {
      store.setVerificationResult(result);
      store.clearPendingRequest();
    }

    return result;
  };

  // Store pending request in Zustand store
  // This triggers:
  // - isRequired = true (modal opens automatically via ModalContext)
  // - pendingRequest stored (modal can execute it)
  // - sessionId and method stored (modal displays correct message)
  useTwoFactorStore.getState().requireTwoFA({
    sessionId,
    method,
    pendingRequest: {
      execute: executeWithCode,
    },
  });

  // Pause execution and wait for user to complete 2FA
  // This promise will resolve when modal stores the result via setVerificationResult()
  const result = await waitForTwoFAResult();

  // Clean up store state after getting result
  useTwoFactorStore.getState().clear();

  // Return the result to the original API call
  // The original mutation promise will now resolve with this result
  return result as ApiResult<T>;
}

/**
 * Handle wrong 2FA code - re-store pending request and wait for retry
 *
 * This is called when user provides a code but backend still requires 2FA (wrong code).
 * It:
 * 1. Re-stores the pending request (so user can retry)
 * 2. Sets error message in store (modal displays it)
 * 3. Waits for user to enter a new code
 * 4. Returns the result once verification succeeds or fails
 *
 * @param response - API response still indicating 2FA is required (wrong code)
 * @param makeRequestWithCode - Function to replay the original request with 2FA code
 * @returns Final API result after retry verification
 */
export function handleWrongTwoFACode<T>(response: ApiResponse<T>): ApiResult<T> {
  const { sessionId, method, pendingRequest, setError } = useTwoFactorStore.getState();

  // Check if we're being called from within executeWithCode (2FA flow already active)
  // If so, just set error and return - don't wait (would cause deadlock)
  const isInActiveFlow = pendingRequest !== undefined;

  if (isInActiveFlow) {
    console.log('DEBUG: handleWrongTwoFACode isInActiveFlow', { response });
    const errorMessage = response.message || 'Invalid verification code. Please try again.';
    // We're in an active 2FA flow - just set error and return
    setError(errorMessage);
    return {
      data: null,
      error: {
        message: TWOFA_VERIFICATION_FAILED_ERROR,
        details: { session_id: sessionId, twofa_method: method },
      },
    };
  }

  // If we're not in an active flow, set error and return
  const errorMessage = response.message || 'Something went wrong. Please try again.';
  setError(errorMessage);
  return {
    data: null,
    error: { message: errorMessage },
  };
}

/**
 * Add 2FA headers to request headers if a code is provided
 *
 * When replaying a request with a 2FA code, these headers must be included:
 * - X-2FA-Session-Id: The session ID from the initial 2FA requirement
 * - X-2FA-Session-Code: The verification code entered by the user
 *
 * @param headers - Request headers object to modify
 * @param twoFactorCode - Optional 2FA verification code
 */
export function addTwoFAHeaders(headers: Record<string, string>, twoFactorCode?: string): void {
  if (twoFactorCode) {
    const store = useTwoFactorStore.getState();
    const sessionId = store.sessionId;
    if (sessionId) {
      headers[TWOFA_HEADERS.SESSION_ID] = sessionId;
      headers[TWOFA_HEADERS.SESSION_CODE] = twoFactorCode;
    }
  }
}

/**
 * Process API response and handle 2FA requirements
 *
 * Main entry point for 2FA handling. Called by apiClient after receiving a response.
 * Determines if 2FA is required and routes to appropriate handler:
 * - Initial requirement: No code provided yet → handleTwoFARequirement()
 * - Wrong code: Code provided but still required → handleWrongTwoFACode()
 * - No 2FA needed: Returns null, normal flow continues
 *
 * @param response - API response to check
 * @param twoFactorCode - Optional 2FA code if this is a retry
 * @param makeRequestWithCode - Function to replay request with 2FA code
 * @returns API result if 2FA was handled, null if no 2FA required
 */
export async function processTwoFAResponse<T>(
  response: ApiResponse<T>,
  twoFactorCode: string | undefined,
  makeRequestWithCode: (code: string) => Promise<ApiResult<T>>
): Promise<ApiResult<T> | null> {
  // Check if response indicates 2FA verification failed (wrong code)
  const isVerificationFailed = response.error === TWOFA_VERIFICATION_FAILED_ERROR;

  // Check if response indicates 2FA is required (initial requirement)
  const isRequired = isTwoFARequired(response);

  // If neither, no 2FA handling needed
  if (!isRequired && !isVerificationFailed) {
    return null; // No 2FA needed, continue normal flow
  }

  // Wrong code: User provided code but verification failed
  if (isVerificationFailed) {
    return handleWrongTwoFACode(response);
  }

  // Initial 2FA requirement: First time seeing TWOFA_REQUIRED (no code provided yet)
  if (!twoFactorCode) {
    return handleTwoFARequirement(response, makeRequestWithCode);
  }

  return null;
}
